import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useReducer,
  useMemo,
  memo,
} from 'react';
import {FemaGoTable} from 'app-ui-componentlibrary';
import {Input, Button, Icon, Pagination, Header} from 'semantic-ui-react';
import {compileContent} from 'common/utils';
import REST from 'go/requests/rest';
import {connectWithCore} from 'framework/hocs';
import contentMap from 'componentLibrary';
import _ from 'lodash';

function reducer(state, action) {
  switch (action.type) {
    case 'SET_SEARCH_TERM':
      if (state.searchTerm === action.payload) {
        return state;
      } else {
        // Reset pageNum when search term changes
        return {...state, searchTerm: action.payload, pageNum: 0};
      }
    case 'SET_SORT_COLUMN':
      const {column, defaultOrder, type} = action.payload;
      if (state.sortColumn.name === column) {
        return {
          ...state,
          sortColumn: {
            name: column,
            type,
            order: state.sortColumn.order === 'ASC' ? 'DESC' : 'ASC',
          },
        };
      } else {
        return {
          ...state,
          sortColumn: {
            name: column,
            type,
            order: defaultOrder || 'ASC',
          },
        };
      }
    case 'SET_PAGE_NUM':
      if (state.pageNum === action.payload) {
        return state;
      }
      return {...state, pageNum: action.payload};
    case 'REFRESH':
      return {...state};
    default:
      return state;
  }
}

const useSearchSortAndPagination = ({
  searchColumns,
  defaultSortColumn,
  pageSize,
  asyncFetchItems,
}) => {
  const [params, dispatchParams] = useReducer(reducer, {
    searchTerm: '',
    sortColumn: defaultSortColumn || {},
    pageNum: 0,
  });
  const [loadingState, setLoadingState] = useState({
    status: 'loading',
    data: [],
    error: null,
    totalItems: 0,
    totalPages: 0,
  });
  const isMounted = useRef(true);

  const setSortColumn = useCallback((column, defaultOrder, type) => {
    dispatchParams({type: 'SET_SORT_COLUMN', payload: {column, defaultOrder, type}});
  }, []);

  const refresh = useCallback(() => {
    dispatchParams({type: 'REFRESH'});
  }, []);

  const setPageNum = useCallback((pageNum) => {
    dispatchParams({type: 'SET_PAGE_NUM', payload: pageNum});
  }, []);

  const setSearchTerm = useCallback((searchTerm) => {
    dispatchParams({type: 'SET_SEARCH_TERM', payload: searchTerm});
  }, []);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const fetch = async () => {
      setLoadingState({...loadingState, status: 'loading', data: [], error: null});
      const {name: sortBy, order: sortDir, type: sortColumnType} = params.sortColumn;
      const searchTerm = params.searchTerm;
      const pageNum = params.pageNum;
      try {
        const {items, totalItems, totalPages} = await asyncFetchItems({
          searchTerm: searchTerm,
          searchColumns: searchTerm ? searchColumns : [],
          sortBy,
          sortDir: sortDir ? sortDir.toLowerCase() : '',
          sortColumnType: sortBy ? sortColumnType : '',
          pageNum: pageSize ? pageNum : undefined,
          pageSize,
        });

        if (isMounted.current) {
          setLoadingState({
            status: 'success',
            data: items,
            error: null,
            totalItems,
            totalPages,
          });
        }
      } catch (error) {
        if (isMounted.current) {
          setLoadingState({
            status: 'error',
            data: [],
            error: String(error),
            totalItems: 0,
            totalPages: 0,
          });
        }
      }
    };
    fetch();
  }, [params, searchColumns, pageSize, asyncFetchItems]);

  return {
    state: loadingState,
    params,
    changeSortColumn: setSortColumn,
    setPageNum,
    changeSearchTerm: setSearchTerm,
    refreshData: refresh,
  };
};

const SearchBar = memo(
  ({
    changeSearchTerm,
    refreshData,
    searchPlaceholder,
    searchRegex,
    searchInstructions,
  }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const allowableCharactersRegex = searchRegex ? new RegExp(searchRegex) : undefined;
    const setDebouncedSearchTerm = useCallback(
      _.debounce((value) => changeSearchTerm(value), 500),
      []
    );

    const setNewSearchTerm = (newSearchTerm, noDebounce) => {
      if (
        !allowableCharactersRegex ||
        allowableCharactersRegex.test(newSearchTerm) ||
        newSearchTerm === ''
      ) {
        setSearchTerm(newSearchTerm);
        if (noDebounce) {
          changeSearchTerm(newSearchTerm);
        } else {
          setDebouncedSearchTerm(newSearchTerm);
        }
      }
    };

    const clearSearchTerm = () => {
      setNewSearchTerm('', true);
    };

    const handleOnSubmit = (e) => {
      e.preventDefault();
      refreshData();
    };

    return (
      <div className="search-bar">
        <form id="search-form" onSubmit={handleOnSubmit}>
          <span>
            <Input style={{paddingBottom: '30px'}}>
              <Input
                aria-label={`Search by term`}
                title={`Search by term`}
                value={searchTerm}
                placeholder={searchPlaceholder}
                onChange={(e, entry) => setNewSearchTerm(entry.value)}
                onFocus={(e) => e.target.select()}
                style={{width: '380px'}}
              />
              <Button primary icon aria-label="Search Button" title="Search button">
                <Icon name="search" fitted />
              </Button>
            </Input>
            {searchTerm !== '' && (
              <a
                id="clear-search"
                href="#"
                className="padding-left"
                onClick={clearSearchTerm}
              >
                Clear search
              </a>
            )}
          </span>
        </form>
        {searchInstructions && <p style={{color: '#d3d3d3'}}>{searchInstructions}</p>}
      </div>
    );
  }
);

const PaginationBar = memo(({totalPages, setPageNum, pageNum}) => {
  let showPrevButton = pageNum + 1 > 1 ? undefined : null;
  let showNextButton = pageNum + 1 < totalPages ? undefined : null;

  const handlePageChange = (e, {activePage}) => {
    setPageNum(activePage - 1);
  };

  return (
    <Pagination
      activePage={pageNum + 1}
      siblingRange={1}
      boundaryRange={1}
      prevItem={showPrevButton}
      nextItem={showNextButton}
      firstItem={showPrevButton}
      lastItem={showNextButton}
      ellipsisItem={undefined}
      totalPages={totalPages}
      onPageChange={handlePageChange}
      size="tiny"
    />
  );
});

function compareValues(a, b, type) {
  if (type === 'string') {
    return a.localeCompare(b);
  } else if (type === 'numeric') {
    return a - b;
  } else if (type === 'date') {
    const dateA = new Date(a);
    const dateB = new Date(b);
    return dateA - dateB;
  } else {
    throw new Error('Invalid type');
  }
}

const filter = (items, searchTerm, searchColumns) => {
  if (searchTerm === '') {
    return items;
  } else {
    return items.filter((attachment) => {
      return searchColumns.some((field) => {
        return attachment[field].toLowerCase().includes(searchTerm.toLowerCase());
      });
    });
  }
};

const sort = (items, sortBy, sortDir, sortColumnType) => {
  if (!sortBy || sortBy === '') {
    return items;
  } else {
    const sortedItems = [...(items || [])].sort((a, b) => {
      if (sortDir.toUpperCase() === 'ASC') {
        return compareValues(a[sortBy], b[sortBy], sortColumnType || 'string');
      } else {
        return compareValues(b[sortBy], a[sortBy], sortColumnType || 'string');
      }
    });
    return sortedItems;
  }
};

const useAsyncFetchItems = ({
  apiConfig,
  dependencies,
  dynComponentProps,
  clientSidePagination,
}) => {
  const restDependencies = () => {
    if (clientSidePagination || !dependencies) return {};
    return _.reduce(
      dependencies,
      (result, item) => {
        _.each(item, (value, key) => {
          result[key] = _.get(dynComponentProps, value, value);
        });
        return result;
      },
      {}
    );
  };

  const fetchItemsRest = async (searchSortPageParams, pageSize) => {
    const {service, action, params} = apiConfig;
    if (service && action && params) {
      const args = {...searchSortPageParams};
      _.each(params, (value, key) => {
        args[key] = _.get(restDependencies(), value, value);
      });
      try {
        const response = await REST[service][action]({...args}).then(
          REST.handler.handleResponse
        );
        return Promise.resolve({
          items: response.data ? response.data : response,
          totalItems: response.pagination
            ? response.pagination.totalItems
            : response.length,
          totalPages: response.pagination
            ? response.pagination.totalPages
            : Math.ceil(response.length / pageSize),
        });
      } catch (error) {
        return Promise.reject(error);
      }
    } else {
      return Promise.reject('No API config provided');
    }
  };

  const fetchItemsDataPath = async ({
    searchTerm,
    searchColumns,
    sortBy,
    sortDir,
    sortColumnType,
    pageNum,
    pageSize,
  }) => {
    const data = _.get(dynComponentProps, 'data', []);
    const filtered = searchTerm ? filter(data, searchTerm, searchColumns) : data;
    const sorted =
      sortBy && sortDir && sortColumnType
        ? sort(filtered, sortBy, sortDir, sortColumnType)
        : filtered;
    const sliced = pageSize
      ? sorted.slice(pageNum * pageSize, (pageNum + 1) * pageSize)
      : sorted;
    return Promise.resolve({
      items: sliced,
      totalItems: filtered.length,
      totalPages: Math.ceil(filtered.length / pageSize),
    });
  };

  return clientSidePagination ? fetchItemsDataPath : fetchItemsRest;
};

const _DynamicSearchableTable = (props) => {
  const {
    tableHeaders,
    header,
    headerSize,
    description,
    headerAligns,
    columnWidths,
    id,
    tableSummary,
    fnDynamicRowContent,
    apiConfig,
    searchConfig,
    sortConfig,
    pageConfig,
    columnTransformation,
    clientSidePagination,
    dependencies,
    noItemsMessage,
    ...dynComponentProps
  } = props;

  const {searchColumns} = searchConfig || {};
  const {defaultSortColumnName, sortableColumns} = sortConfig || {};
  const {pageSize} = pageConfig || {};
  const defaultSortColumn = {
    name: defaultSortColumnName,
    type: _.get(sortableColumns, [defaultSortColumnName, 'type'], 'string'),
    order: _.get(sortableColumns, [defaultSortColumnName, 'defaultOrder'], 'ASC'),
  };

  //dependencies or dataPath should be loaded in the context by the time the component is rendered.
  //updates to these will not be automatically reflected in the component later on.
  const asyncFetchItems = useMemo(
    () =>
      useAsyncFetchItems({
        apiConfig,
        dependencies,
        dynComponentProps,
        clientSidePagination,
      }),
    []
  );

  const {
    state,
    params: {searchTerm, sortColumn: currentSortColumn, pageNum},
    changeSortColumn,
    setPageNum,
    changeSearchTerm,
    refreshData,
  } = useSearchSortAndPagination({
    searchColumns,
    defaultSortColumn,
    pageSize,
    asyncFetchItems,
  });

  const renderHeader = header && (
    <Header as={headerSize || 'h3'} content={compileContent(header, props)} />
  );
  const renderDescription = description && <p>{description}</p>;

  const tableHeadersAsId = _.map(tableHeaders, (header) => {
    return _.camelCase(header) + (id ? id : '');
  });

  const renderTableHeaders = tableHeaders && (
    <FemaGoTable.Header>
      <FemaGoTable.Row>
        {_.map(tableHeaders, ({columnName, content}, index) => {
          let sortProps = {};
          if (columnName in (sortableColumns || {})) {
            const {defaultOrder, type: sortType} = sortableColumns[columnName];
            sortProps.onClick = () =>
              changeSortColumn(columnName, defaultOrder.toUpperCase(), sortType);
            if (currentSortColumn.name === columnName) {
              sortProps.className = `sorted ${
                currentSortColumn.order === 'ASC' ? 'ascending' : 'descending'
              }`;
            }
          }

          return (
            <FemaGoTable.HeaderCell
              textAlign={_.get(headerAligns, index, '')}
              width={_.get(columnWidths, index, '')}
              id={_.get(tableHeadersAsId, index, '')}
              key={index}
              {...sortProps}
            >
              {compileContent(content, props)}
            </FemaGoTable.HeaderCell>
          );
        })}
      </FemaGoTable.Row>
    </FemaGoTable.Header>
  );

  return (
    <React.Fragment>
      {renderHeader}
      {renderDescription}
      {searchConfig && (
        <SearchBar {...{changeSearchTerm, refreshData, ...searchConfig}} />
      )}
      {state.status === 'success' && (
        <FemaGoTable type="static" className="sortable" summary={tableSummary}>
          {renderTableHeaders}
          <FemaGoTable.Body>
            {_.map(state.data, (item, index) => {
              return (
                <FemaGoTable.Row key={index}>
                  {_.map(tableHeaders, ({columnName}, index) => {
                    const cellProps = {
                      textAlign: _.get(headerAligns, index, ''),
                      width: _.get(columnWidths, index, ''),
                      key: `${columnName}${index}`,
                    };
                    if (fnDynamicRowContent) {
                      const row = fnDynamicRowContent(item, index);
                      if (!row[columnName]) {
                        return (
                          <FemaGoTable.TextCell {...cellProps}>
                            {_.get(item, columnName, '')}
                          </FemaGoTable.TextCell>
                        );
                      }
                      return row[columnName]({...cellProps});
                    } else {
                      const columnValue = _.get(item, columnName, '');
                      return (
                        <FemaGoTable.TextCell {...cellProps}>
                          {compileContent(
                            _.get(columnTransformation, columnName, columnValue),
                            {[columnName]: columnValue},
                            true
                          )}
                        </FemaGoTable.TextCell>
                      );
                    }
                  })}
                </FemaGoTable.Row>
              );
            })}
            {state.totalItems === 0 && (
              <FemaGoTable.Row>
                <FemaGoTable.TextCell colSpan={tableHeaders.length} textAlign="center">
                  {noItemsMessage || 'No results found'}
                </FemaGoTable.TextCell>
              </FemaGoTable.Row>
            )}
          </FemaGoTable.Body>
        </FemaGoTable>
      )}
      {state.status === 'error' && (
        <div>
          <p>{state.error}</p>
          <Button onClick={refreshData} title="Refresh Button">
            Refresh
          </Button>
        </div>
      )}
      {state.status === 'loading' && pageSize && (
        <FemaGoTable type="static" className="sortable" summary={tableSummary}>
          {renderTableHeaders}
        </FemaGoTable>
      )}
      {pageSize && state.totalPages > 1 && (
        <PaginationBar {...{totalPages: state.totalPages, setPageNum, pageNum}} />
      )}
      {state.status === 'loading' && <Icon loading name="spinner" size="large" />}
    </React.Fragment>
  );
};

export const WithContentMap = (props) => (
  <_DynamicSearchableTable {...props} {...{contentMap}} />
);
export const DynamicSearchableTable = connectWithCore(WithContentMap);
export default DynamicSearchableTable;
